import { ArrowLeft, BellSlash, Check, LinkSimple, Trash, X } from "@phosphor-icons/react";
import { Button, DropdownMenuItem, DropdownMenuSeparator, Kebab, useConfirm } from "@hulahoop/ui";
import type { ReactNode } from "react";
import { CountPill } from "@/components/shared/data-display/CountPill";
import type { CommentsLabels } from "@/domain/types/commentsLabels";
import { IconTooltip } from "./IconTooltip";

export function CommentsPanelHeader({
  thread,
  count,
  filter,
  busy,
  labels,
  onBack,
  onClose,
  onResolve,
  onMute,
  onDelete,
  onCopy,
}: {
  thread: { resolved: boolean; muted: boolean; own: boolean } | null;
  count: number;
  filter: ReactNode;
  busy: boolean;
  labels: CommentsLabels;
  onBack: () => void;
  onClose: () => void;
  onResolve: () => void;
  onMute: () => void;
  onDelete: () => Promise<boolean>;
  onCopy: () => void;
}) {
  const { confirm, dialog } = useConfirm();
  return (
    <>
      {dialog}
      <div
        className={
          thread
            ? "flex min-h-(--control-h-lg) shrink-0 items-center gap-1 border-b border-border px-2"
            : "flex min-h-(--control-h-lg) shrink-0 items-center gap-1.5 border-b border-border px-2"
        }
      >
        {thread ? (
          <>
            <IconTooltip label={labels.back}>
              <Button variant="ghost" size="icon-sm" aria-label={labels.back} onClick={onBack}>
                <ArrowLeft />
              </Button>
            </IconTooltip>
            <span className="text-(length:--font-ui-sm) font-medium">{labels.thread}</span>
          </>
        ) : (
          <>
            <span className="px-1 text-(length:--font-ui) font-medium">{labels.title}</span>
            {count > 0 && <CountPill n={count} />}
          </>
        )}
        <span className="ml-auto flex items-center gap-0.5">
          {thread ? (
            <>
              <Button variant="outline" size="xs" disabled={busy} onClick={onResolve}>
                <Check />
                {thread.resolved ? labels.reopen : labels.resolve}
              </Button>
              <Kebab label={labels.actions} pinned>
                <DropdownMenuItem onClick={onCopy}>
                  <LinkSimple />
                  {labels.copy_link}
                </DropdownMenuItem>
                <DropdownMenuItem disabled={busy} onClick={onMute}>
                  <BellSlash />
                  {thread.muted ? labels.unmute : labels.mute}
                </DropdownMenuItem>
                {thread.own && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      disabled={busy}
                      onClick={() =>
                        void confirm({
                          title: labels.delete_title,
                          description: labels.delete_description,
                          actionLabel: labels.delete,
                          destructive: true,
                          run: async () => {
                            if (!(await onDelete())) throw new Error(labels.error);
                          },
                        })
                      }
                    >
                      <Trash />
                      {labels.delete}
                    </DropdownMenuItem>
                  </>
                )}
              </Kebab>
            </>
          ) : (
            filter
          )}
          <IconTooltip label={labels.close}>
            <Button variant="ghost" size="icon-sm" aria-label={labels.close} onClick={onClose}>
              <X />
            </Button>
          </IconTooltip>
        </span>
      </div>
    </>
  );
}
